import { createCacheKey, ENDPOINTS, fetchResource, useCreatePayload } from 'hammock';
import { useCallback } from 'react';
import { useMutation, useQuery } from 'react-query';

import { FontFamily, WebFontResource } from './types';

const mapCustomFont = ({ name, url, id }: FontFamily): WebFontResource => ({
  id,
  family: name,
  url,
  type: 'custom',
});

export const useCustomFonts = () => {
  const { getAll: getFontFamilies, create, delete: deleteFont } = useCreatePayload(
    ENDPOINTS.fontFamilies,
  );

  const fontFamiliesgetPayload = getFontFamilies({ query: {} });
  const fontFamiilyCacheKey = createCacheKey(fontFamiliesgetPayload);

  const { data: fonts, isLoading: loading, refetch: refetchFonts } = useQuery<WebFontResource[]>({
    queryKey: fontFamiilyCacheKey,
    queryFn: () =>
      fetchResource<FontFamily[]>(fontFamiliesgetPayload).then((data) => {
        return data.map(mapCustomFont);
      }),
  });

  const { mutateAsync: mutateCreateFontFamily } = useMutation<
    WebFontResource,
    unknown,
    { name: string; url: string }
  >({
    mutationFn: (payload) => fetchResource(create({ body: payload })),
    onSuccess: () => {
      refetchFonts();
    },
  });

  const { mutate: mutateDeleteFontFamily } = useMutation<{ id: string }, unknown, { id: string }>({
    mutationFn: (payload) => fetchResource(deleteFont({ id: payload.id })).then(() => payload),
    onSuccess: () => {
      refetchFonts();
    },
  });

  const createFontFamily = useCallback(
    // if the font family name contains anything other than letter or spaces, add quotes
    (name: string, url: string) =>
      mutateCreateFontFamily({ name: /[^a-zA-Z ]+/.test(name) ? `"${name}"` : name, url }),
    [],
  );

  const deleteFontFamily = useCallback((id: string) => mutateDeleteFontFamily({ id }), []);
  return {
    fonts,
    loading,
    createFontFamily,
    deleteFontFamily,
  };
};
